import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { NumberRangeInput } from "@/components/ui/number-range-input";
import { TrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseFilterProps, Filter, FilterData } from "../_types";

function VehicleNumberRangeFilterComponent({
  labelKey,
  unit,
  selectedValues,
  onSelectionChange,
  disabled,
}: BaseFilterProps & { labelKey: string; unit?: string }) {
  const { t } = useTranslation(["common", "masterdata"]);

  const min = selectedValues[0] ?? "";
  const max = selectedValues[1] ?? "";

  const handleMinChange = (value: string) => {
    onSelectionChange(value === "" && max === "" ? [] : [value, max]);
  };

  const handleMaxChange = (value: string) => {
    onSelectionChange(min === "" && value === "" ? [] : [min, value]);
  };

  const handleClear = () => {
    onSelectionChange([]);
  };

  const hasValues = min !== "" || max !== "";
  const label = `${t(labelKey)}${unit ? ` (${unit})` : ""}`;

  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <NumberRangeInput
          min={min}
          max={max}
          onMinChange={handleMinChange}
          onMaxChange={handleMaxChange}
          disabled={disabled}
          className="min-w-0 flex-1"
        />
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Icon}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClear();
          }}
          className="shrink-0"
          disabled={!hasValues || disabled}
          aria-label={t("common:Remove")}
        >
          <TrashIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export class VehicleMileageRangeFilter extends Filter<Record<string, never>, Record<string, never>> {
  readonly id: string = "mileage";
  readonly labelSingular = "masterdata:Mileage";
  readonly labelPlural = "masterdata:Mileage";
  readonly placeholder = "masterdata:Mileage";
  readonly searchPlaceholder = "";
  readonly emptyText = "";
  body = {};
  parameters = {};

  constructor({ id }: { id?: string } = {}) {
    super();
    if (id) this.id = id;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <VehicleNumberRangeFilterComponent labelKey="masterdata:Mileage" unit="km" {...props} />;
  }
}

export class VehiclePowerKwRangeFilter extends Filter<Record<string, never>, Record<string, never>> {
  readonly id: string = "powerKw";
  readonly labelSingular = "masterdata:PowerKw";
  readonly labelPlural = "masterdata:PowerKw";
  readonly placeholder = "masterdata:PowerKw";
  readonly searchPlaceholder = "";
  readonly emptyText = "";
  body = {};
  parameters = {};

  constructor({ id }: { id?: string } = {}) {
    super();
    if (id) this.id = id;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <VehicleNumberRangeFilterComponent labelKey="masterdata:PowerKw" unit="kW" {...props} />;
  }
}

export class VehiclePowerPsRangeFilter extends Filter<Record<string, never>, Record<string, never>> {
  readonly id: string = "powerPs";
  readonly labelSingular = "masterdata:PowerPs";
  readonly labelPlural = "masterdata:PowerPs";
  readonly placeholder = "masterdata:PowerPs";
  readonly searchPlaceholder = "";
  readonly emptyText = "";
  body = {};
  parameters = {};

  constructor({ id }: { id?: string } = {}) {
    super();
    if (id) this.id = id;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <VehicleNumberRangeFilterComponent labelKey="masterdata:PowerPs" unit="PS" {...props} />;
  }
}
